import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import TextEditor from 'flarum/common/components/TextEditor';
import Switch from 'flarum/common/components/Switch';
import classList from 'flarum/common/utils/classList';
export { default as extend } from './extend';
function noticeMessage() {
  return app.forum.attribute('hliMediaManagerNoticeMessage') || null;
}

/**
 * Wraps the uploader's upload() so that, while a notice is configured, no
 * upload goes through unless the user has accepted the notice in the media
 * manager. Every upload path in fof/upload (the media manager's upload
 * button, drag & drop onto the modal or the composer, pasting into the
 * composer) calls uploader.upload() at the time of the upload, so gating it
 * here covers all of them. Idempotent, so it's safe to call from both the
 * TextEditor and the modal.
 */
function gateUploader(uploader) {
  if (!uploader || uploader.uploadNoticeGated) return;
  uploader.uploadNoticeGated = true;
  const original = uploader.upload.bind(uploader);
  uploader.upload = (files, addBBcode) => {
    if (noticeMessage() && !uploader.uploadNoticeAccepted) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('hli-media-manager-notice.forum.upload_blocked'));
      return Promise.resolve();
    }
    return original(files, addBBcode);
  };

  // The acceptance is never persisted: it only covers the next upload and
  // has to be given again afterwards.
  uploader.on('uploaded', () => {
    uploader.uploadNoticeAccepted = false;
  });
}
app.initializers.add('hli-media-manager-notice', () => {
  // fof/upload creates the composer's uploader in its own TextEditor oninit
  // extension, which runs before ours (this extension requires fof/upload).
  extend(TextEditor.prototype, 'oninit', function () {
    gateUploader(this.uploader);
  });

  // fof/upload's FileManagerModal is imported synchronously by its own
  // FileManagerButton (see fof/upload's forum/components/FileManagerButton.tsx),
  // so its module is already registered by the time any extension's
  // initializer runs. The string-path form still works either way, since it
  // defers via flarum.reg.onLoad if the module isn't registered yet.
  //
  // Non-core modules must use the `ext:<vendor>/<package>/<path>` form
  // (see ExportRegistry.namespaceAndIdFromPath) — a bare `fof-upload/...`
  // path isn't recognized and throws at initialization.
  extend('ext:fof/upload/forum/components/FileManagerModal', 'oninit', function () {
    const uploader = this.uploader;
    gateUploader(uploader);

    // Each time the media manager is opened, the notice has to be accepted again.
    uploader.uploadNoticeAccepted = false;
  });
  extend('ext:fof/upload/forum/components/FileManagerModal', 'onremove', function () {
    this.uploader.uploadNoticeAccepted = false;
  });
  override('ext:fof/upload/forum/components/FileManagerModal', 'view', function (original) {
    const result = original();
    const message = noticeMessage();
    if (!message) return result;
    const uploader = this.uploader;
    const accepted = !!uploader.uploadNoticeAccepted;

    // The modal doesn't expose an extension point for its header area, so the
    // notice is spliced directly into view()'s vnode tree, right after the
    // ".Modal-header" div (shared by both the "My media" and "Shared media"
    // tabs, so the notice stays visible across both) and before the file
    // grid. Defensive: if fof/upload's markup ever changes shape, this
    // silently no-ops instead of breaking the modal.
    const modalContent = result?.children?.[0];
    const siblings = modalContent?.children;
    const headerIndex = siblings?.findIndex(child => child?.attrs?.className === 'Modal-header') ?? -1;
    if (siblings && headerIndex !== -1) {
      siblings.splice(headerIndex + 1, 0, m("div", {
        className: classList('hli-MediaManagerNotice', !accepted && 'hli-MediaManagerNotice--pending')
      }, m("i", {
        className: "fas fa-bullhorn",
        "aria-hidden": "true"
      }), m("div", {
        className: "hli-MediaManagerNotice-body"
      }, m("span", {
        className: "hli-MediaManagerNotice-message"
      }, message), m(Switch, {
        className: "hli-MediaManagerNotice-accept",
        state: accepted,
        disabled: uploader.uploading,
        onchange: checked => uploader.uploadNoticeAccepted = checked
      }, app.translator.trans('hli-media-manager-notice.forum.accept_label')))));
    }
    if (!accepted) {
      // ".fof-modal-buttons" only holds upload buttons (the "My media" upload
      // button and the "Shared media" one), so all of them are disabled until
      // the notice is accepted. The upload() gate above still blocks drag &
      // drop; the notice's --pending class lets the stylesheet hide the modal's
      // "release to upload" overlay in the meantime (see less/forum.less).
      const buttons = siblings?.find(child => child?.attrs?.className?.split(' ').includes('fof-modal-buttons'));
      buttons?.children?.forEach(button => {
        if (button?.attrs) button.attrs.disabled = true;
      });
    }
    return result;
  });

  // fof/upload adds its direct "upload" button (key 'fof-upload') to the
  // composer's controlItems in its own addUploadButton() (called before
  // ours, since this extension requires fof/upload). When a notice is
  // configured, the direct upload button is removed so uploads are only
  // possible via the media manager, where the notice is shown.
  extend(TextEditor.prototype, 'controlItems', function (items) {
    if (!noticeMessage()) return;
    items.remove('fof-upload');
  });
});